import { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Plus, Trash2, Loader2, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { useAppContext } from "@/lib/onboardingContext";
import { getCurrencySymbol } from "@/lib/currency";
import { toast } from "sonner";

const BRAND_GREEN = "#2d8653";
const BRAND_ORANGE = "#e76f00";
const BRAND_DARK = "#1a1a1a";

const SERVICES = [
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
  { value: "all-day", label: "All day" },
] as const;

type ServiceValue = typeof SERVICES[number]["value"];

interface RevenueLogRow {
  id: string;
  entry_date: string;
  service: ServiceValue;
  covers: number;
  total_revenue: number;
  avg_spend_per_cover: number;
  revenue_per_cover: number;
}

function todayStr(): string {
  return new Date().toISOString().split("T")[0];
}

function shortDay(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short" });
}

export default function DailyRevenueLog() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();
  const { onboarding } = useAppContext();
  const cs = getCurrencySymbol(onboarding.country || onboarding.city || "");

  const [rows, setRows] = useState<RevenueLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [date, setDate] = useState(todayStr());
  const [service, setService] = useState<ServiceValue>("all-day");
  const [covers, setCovers] = useState("");
  const [revenue, setRevenue] = useState("");
  const [avgSpend, setAvgSpend] = useState("");

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = supabase
        .from("daily_revenue_logs")
        .select("id, entry_date, service, covers, total_revenue, avg_spend_per_cover, revenue_per_cover")
        .eq("user_id", user.id)
        .order("entry_date", { ascending: false })
        .limit(60);
      if (activeLocation?.id) q = q.eq("location_id", activeLocation.id);
      const { data, error } = await q;
      if (cancelled) return;
      if (error) {
        toast.error("Couldn't load revenue logs");
      } else {
        setRows((data ?? []) as RevenueLogRow[]);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, activeLocation?.id]);

  const computedRpc = useMemo(() => {
    const c = Number(covers);
    const r = Number(revenue);
    if (!c || !r) return 0;
    return r / c;
  }, [covers, revenue]);

  // Build a 7-day window ending today (oldest → newest) — sums all services per day.
  const last7 = useMemo(() => {
    const out: { date: string; label: string; revenue: number; rpc: number }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const iso = d.toISOString().split("T")[0];
      const dayRows = rows.filter(r => r.entry_date === iso);
      const totalRev = dayRows.reduce((s, r) => s + Number(r.total_revenue), 0);
      const totalCovers = dayRows.reduce((s, r) => s + r.covers, 0);
      out.push({
        date: iso,
        label: shortDay(iso),
        revenue: totalRev,
        rpc: totalCovers > 0 ? totalRev / totalCovers : 0,
      });
    }
    return out;
  }, [rows]);

  const weekTotal = last7.reduce((s, d) => s + d.revenue, 0);

  const handleSave = async () => {
    if (!user) return;
    const c = Number(covers);
    const r = Number(revenue);
    const a = avgSpend ? Number(avgSpend) : (c > 0 ? r / c : 0);
    if (!date || c < 0 || r < 0) {
      toast.error("Enter a valid date and non-negative numbers");
      return;
    }
    setSaving(true);
    const { data, error } = await supabase
      .from("daily_revenue_logs")
      .insert({
        user_id: user.id,
        location_id: activeLocation?.id ?? null,
        entry_date: date,
        service,
        covers: c,
        total_revenue: r,
        avg_spend_per_cover: a,
      })
      .select("id, entry_date, service, covers, total_revenue, avg_spend_per_cover, revenue_per_cover")
      .single();
    setSaving(false);
    if (error) {
      toast.error("Couldn't save entry");
      return;
    }
    setRows(prev => [data as RevenueLogRow, ...prev]);
    setCovers("");
    setRevenue("");
    setAvgSpend("");
    toast.success("Logged");
  };

  const handleDelete = async (id: string) => {
    const prev = rows;
    setRows(rows.filter(r => r.id !== id));
    const { error } = await supabase.from("daily_revenue_logs").delete().eq("id", id);
    if (error) {
      setRows(prev);
      toast.error("Couldn't delete");
    }
  };

  const fmtMoney = (n: number) =>
    n >= 1000 ? `${cs}${(n / 1000).toFixed(1)}K` : `${cs}${Math.round(n).toLocaleString()}`;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${BRAND_GREEN}1a` }}
        >
          <TrendingUp className="w-4 h-4" style={{ color: BRAND_GREEN }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-lg font-medium" style={{ color: BRAND_DARK }}>
            Daily Revenue Log
          </h2>
          <p className="text-xs text-muted-foreground">
            Track service-level revenue and revenue per cover
          </p>
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">7-day total</div>
          <div className="text-lg font-medium tabular-nums" style={{ color: BRAND_DARK }}>
            {fmtMoney(weekTotal)}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
        <div className="col-span-2 lg:col-span-1">
          <Label className="text-xs">Date</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Service</Label>
          <Select value={service} onValueChange={(v) => setService(v as ServiceValue)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {SERVICES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Covers</Label>
          <Input type="number" min="0" value={covers} onChange={e => setCovers(e.target.value)} placeholder="0" />
        </div>
        <div>
          <Label className="text-xs">Revenue ({cs})</Label>
          <Input type="number" min="0" step="0.01" value={revenue} onChange={e => setRevenue(e.target.value)} placeholder="0" />
        </div>
        <div>
          <Label className="text-xs">Avg spend ({cs})</Label>
          <Input
            type="number" min="0" step="0.01"
            value={avgSpend}
            onChange={e => setAvgSpend(e.target.value)}
            placeholder={computedRpc > 0 ? computedRpc.toFixed(2) : "auto"}
          />
        </div>
        <div className="col-span-2 lg:col-span-1 flex items-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full gap-2"
            style={{ backgroundColor: BRAND_GREEN, color: "white" }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </Button>
        </div>
      </div>

      {/* Auto-calc preview */}
      {(covers || revenue) && (
        <div className="mb-5 text-xs text-muted-foreground">
          Revenue per cover:{" "}
          <span className="font-medium tabular-nums" style={{ color: BRAND_DARK }}>
            {computedRpc > 0 ? `${cs}${computedRpc.toFixed(2)}` : "—"}
          </span>
        </div>
      )}

      {/* 7-day chart */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            Last 7 days
          </span>
        </div>
        <div className="h-48 w-full">
          <ResponsiveContainer>
            <BarChart data={last7} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#666" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#666" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtMoney(Number(v))} width={50} />
              <Tooltip
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
                contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }}
                formatter={(value: any, name) => {
                  if (name === "revenue") return [`${cs}${Math.round(Number(value)).toLocaleString()}`, "Revenue"];
                  return [value, name];
                }}
              />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                {last7.map((d, i) => (
                  <Cell key={i} fill={d.revenue > 0 ? BRAND_GREEN : "#e5e7eb"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent entries */}
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
          Recent entries
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4">
            No entries yet. Log your first day above.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.slice(0, 8).map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2 text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="tabular-nums text-muted-foreground w-24 shrink-0">
                    {r.entry_date}
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${BRAND_ORANGE}1a`, color: BRAND_ORANGE }}
                  >
                    {SERVICES.find(s => s.value === r.service)?.label ?? r.service}
                  </span>
                  <span className="text-muted-foreground hidden sm:inline">
                    {r.covers} covers
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="tabular-nums" style={{ color: BRAND_DARK }}>
                    {cs}{Math.round(Number(r.total_revenue)).toLocaleString()}
                  </span>
                  <span className="tabular-nums text-xs text-muted-foreground hidden sm:inline">
                    {cs}{Number(r.revenue_per_cover).toFixed(2)}/cover
                  </span>
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => handleDelete(r.id)}
                    aria-label="Delete entry"
                  >
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}